import { themeI18n } from '../utils/i18n_helper';
import { useState, useRef, useMemo } from 'react';
import { formatHour } from '../utils/time.js';
import '../../styles/components/store-location.scss';
import Icons from './Icons';

export const StoreLocation = ({ store, map, setActiveStore }) => {
  const [accordionOpen, setAccordionOpen] = useState(false);

  const accordionContentRef = useRef(null);

  const accordionMaxHeight = useMemo(() => {
    if (accordionOpen && accordionContentRef.current) {
      return `${accordionContentRef.current.offsetHeight}px`;
    } else {
      return '0px';
    }
  }, [accordionOpen]);

  return (
    <div className={accordionOpen ? 'store-location store-location--accordion-open': 'store-location'}>
      {setActiveStore && (
        <div className='store-location__name'>
          {map ? (
            <button id={`store-${store.handle}`} className='store-location__name store-location__name--clickable' onClick={()=> {
              map.setCenter({lat: parseFloat(store.latitude), lng: parseFloat(store.longitude)});
              map.setZoom(15);

              setActiveStore(store);
            }}>{store.name}</button>
          ):(
            <p className='store-location__name' >{store.name}</p>
          )}

          <button
            className='store-location__accordion-icon'
            onClick={()=> {
              setAccordionOpen(!accordionOpen);
            }}
            aria-expanded={accordionOpen}
          >
            <Icons icon='caret' size='small' />
          </button>
        </div>
      )}

      {setActiveStore && (
        <div className='store-location__info'>
          <p className='store-location__address text-small'>{store.address}</p>

          <p className='store-location__phone text-small'>{store.phone}</p>

          <p className='store-location__distance text-small' title={themeI18n('store_locator.distance')}>
            {store.distance ? `${store.distance.toFixed(2)} km` : null}
          </p>
        </div>
      )}

      <div>
        <OpenIndicator store={store} />

        <div style={{"--max-height": setActiveStore ? accordionMaxHeight : 'unset'}} className='store-location__accordion-content-wrapper'>
          <div ref={accordionContentRef} className='store-location__accordion-content'>
            <p className='store-location__opening-hours-title text-small'>
              {themeI18n('store_locator.opening_hours')}
            </p>

            <div className='store-location__opening-hours-wrapper'>
              <div className='store-location__opening-hours-day'>
                <p className='store-location__opening-hours-day-name'>{themeI18n('store_locator.days.1')}</p>
                <p className='store-location__opening-hours-time'>
                  {formatHour(store.monday_hours?.split("-")[0].split(":")[0], store.monday_hours?.split("-")[0].split(":")[1], true)} - {formatHour(store.monday_hours?.split("-")[1].split(":")[0], store.monday_hours?.split("-")[1].split(":")[1], true)}
                </p>
              </div>
              <div className='store-location__opening-hours-day'>
                <p className='store-location__opening-hours-day-name'>{themeI18n('store_locator.days.2')}</p>
                <p className='store-location__opening-hours-time'>
                  {formatHour(store.tuesday_hours?.split("-")[0].split(":")[0], store.tuesday_hours?.split("-")[0].split(":")[1], true)} - {formatHour(store.tuesday_hours?.split("-")[1].split(":")[0], store.tuesday_hours?.split("-")[1].split(":")[1], true)}
                </p>
              </div>
              <div className='store-location__opening-hours-day'>
                <p className='store-location__opening-hours-day-name'>{themeI18n('store_locator.days.3')}</p>
                <p className='store-location__opening-hours-time'>
                  {formatHour(store.wednesday_hours?.split("-")[0].split(":")[0], store.wednesday_hours?.split("-")[0].split(":")[1], true)} - {formatHour(store.wednesday_hours?.split("-")[1].split(":")[0], store.wednesday_hours?.split("-")[1].split(":")[1], true)}
                </p>
              </div>
              <div className='store-location__opening-hours-day'>
                <p className='store-location__opening-hours-day-name'>{themeI18n('store_locator.days.4')}</p>
                <p className='store-location__opening-hours-time'>
                  {formatHour(store.thursday_hours?.split("-")[0].split(":")[0], store.thursday_hours?.split("-")[0].split(":")[1], true)} - {formatHour(store.thursday_hours?.split("-")[1].split(":")[0], store.thursday_hours?.split("-")[1].split(":")[1], true)}
                </p>
              </div>
              <div className='store-location__opening-hours-day'>
                <p className='store-location__opening-hours-day-name'>{themeI18n('store_locator.days.5')}</p>
                <p className='store-location__opening-hours-time'>
                  {formatHour(store.friday_hours?.split("-")[0].split(":")[0], store.friday_hours?.split("-")[0].split(":")[1], true)} - {formatHour(store.friday_hours?.split("-")[1].split(":")[0], store.friday_hours?.split("-")[1].split(":")[1], true)}
                </p>
              </div>
              <div className='store-location__opening-hours-day'>
                <p className='store-location__opening-hours-day-name'>{themeI18n('store_locator.days.6')}</p>
                <p className='store-location__opening-hours-time'>
                  {formatHour(store.saturday_hours?.split("-")[0].split(":")[0], store.saturday_hours?.split("-")[0].split(":")[1], true)} - {formatHour(store.saturday_hours?.split("-")[1].split(":")[0], store.saturday_hours?.split("-")[1].split(":")[1], true)}
                </p>
              </div>
              <div className='store-location__opening-hours-day'>
                <p className='store-location__opening-hours-day-name'>{themeI18n('store_locator.days.0')}</p>
                <p className='store-location__opening-hours-time'>
                  {formatHour(store.sunday_hours?.split("-")[0].split(":")[0], store.sunday_hours?.split("-")[0].split(":")[1], true)} - {formatHour(store.sunday_hours?.split("-")[1].split(":")[0], store.sunday_hours?.split("-")[1].split(":")[1], true)}
                </p>
              </div>
            </div>

            <a href={`http://maps.google.com/maps?daddr=${store.address}`} className='store-location__directions-link link' target='_blank' rel='noopener'>
              <Icons icon='directions' />
              {themeI18n('store_locator.get_directions')}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

const OpenIndicator = ({ store }) => {
  const currentDay = new Date().getDay();
  const currentDate = new Date();
  const storeHours = {};
  const dayHoursKeys = ['sunday_hours', 'monday_hours', 'tuesday_hours', 'wednesday_hours', 'thursday_hours', 'friday_hours', 'saturday_hours'];

  dayHoursKeys.forEach((day, index) => {
    storeHours[index] = {
      open: store[day]?.split("-")[0].split(":")[0],
      openMinutes: store[day]?.split("-")[0].split(":")[1],
      close: store[day]?.split("-")[1].split(":")[0],
      closeMinutes: store[day]?.split("-")[1].split(":")[1]
    }
  });

  const storeOpeningDate = new Date();
  storeOpeningDate.setHours(storeHours[currentDay].open);
  storeOpeningDate.setMinutes(storeHours[currentDay].openMinutes);
  const storeClosingDate = new Date();
  storeClosingDate.setHours(storeHours[currentDay].close);
  storeClosingDate.setMinutes(storeHours[currentDay].closeMinutes);


  const isOpen = currentDate >= storeOpeningDate && currentDate <= storeClosingDate && storeHours[currentDay].close && storeHours[currentDay].open;

  if (isOpen) {
    return (
      <span className='store-location__open-indicator'>
        <span className='store-location__open-indicator-icon store-location__open-indicator-icon--open'/>
        <span className='store-location__open-indicator-text-blurb'>
          {themeI18n('store_locator.open_until')}
          <span className='store-location__open-indicator-text-blurb-hour'>
            {formatHour(storeHours[currentDay].close, storeHours[currentDay].closeMinutes)}
          </span>
        </span>
      </span>
    )
  } else {
    const isOpeningLater = currentDate < storeOpeningDate;

    if (isOpeningLater) {
      return (
        <span className='store-location__open-indicator'>
          <span className='store-location__open-indicator-icon store-location__open-indicator-icon--closed'/>
          <span className='store-location__open-indicator-text-blurb'>
            {themeI18n('store_locator.closed')}{themeI18n('store_locator.opens_at')}
            <span className='store-location__open-indicator-text-blurb-hour'>
              {formatHour(storeHours[currentDay].open, storeHours[currentDay].openMinutes)}
            </span>
          </span>
        </span>
      )
    } else {
      let nextOpenDay;

      for (let i = currentDay + 1; i <= 6; i++) {
        if (storeHours[i].open) {
          nextOpenDay = i;
          break;
        }
      }

      if (!nextOpenDay) {
        for (let i = 0; i <= currentDay; i++) {
          if (storeHours[i].open) {
            nextOpenDay = i;
            break;
          }
        }
      }

      return (
        <span className='store-location__open-indicator'>
          <span className='store-location__open-indicator-icon store-location__open-indicator-icon--closed'/>
          <span className='store-location__open-indicator-text-blurb'>
            {themeI18n('store_locator.closed')}{themeI18n('store_locator.opens_on')}
            {themeI18n(`store_locator.days.${nextOpenDay}`)}
            {themeI18n('store_locator.at')}
            <span className='store-location__open-indicator-text-blurb-hour'>
              {formatHour(storeHours[nextOpenDay].open, storeHours[nextOpenDay].openMinutes)}
            </span>
          </span>
        </span>
      )
    }
  }
}
